import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import Logo from "@/components/Logo";
import {
  CloudUpload, QrCode, IndianRupee, Printer, ShieldCheck,
  Zap, Building2, ExternalLink, Sparkles,
} from "lucide-react";

const APP_VERSION = "1.1.0";
const APP_BUILD_DATE = "Feb 2026";

const FEATURES = [
  { icon: QrCode, title: "QR-based walk-in prints",
    body: "Customers scan the shop QR, upload files and pay online — no login required." },
  { icon: CloudUpload, title: "Cloud file storage",
    body: "PDFs and images stored securely on Emergent Object Storage with signed URLs." },
  { icon: IndianRupee, title: "Live Razorpay payments",
    body: "Real-time UPI, cards and netbanking with automatic 96/4 wallet split." },
  { icon: Printer, title: "Silent auto-print",
    body: "Windows Connector prints jobs on the retailer's printer with zero popups." },
  { icon: ShieldCheck, title: "JWT + role-based access",
    body: "Retailer, distributor, admin portals with granular permissions." },
  { icon: Zap, title: "Multi-language UI",
    body: "English, Telugu and Hindi with dark & light themes." },
];

export default function About() {
  return (
    <div className="space-y-6 max-w-5xl" data-testid="about-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">About</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">
          About MaaPrint
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          The story, the tech and the team behind the platform.
        </p>
      </div>

      {/* Hero */}
      <Card
        className="p-8 border-brand-green/40 relative overflow-hidden bg-gradient-to-br from-brand-blue to-brand-green text-white"
        data-testid="about-hero"
      >
        <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />
        <div className="relative">
          <Logo size={44} />
          <p className="mt-6 text-xs uppercase tracking-widest text-white/70">
            Cloud print platform
          </p>
          <h2 className="mt-1 text-3xl sm:text-4xl font-display font-semibold leading-tight">
            MaaPrint
            <span className="block text-lg font-normal text-white/90 mt-1">
              Smart Cloud Printing Platform
            </span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-white/90 leading-relaxed max-w-2xl">
            MaaPrint is a cloud printing platform that allows customers to scan a shop QR code,
            upload documents, make online payments, and print automatically — without any manual
            work from the shop owner.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Badge className="bg-white/15 text-white border-white/25 backdrop-blur-sm">
              v{APP_VERSION}
            </Badge>
            <Badge className="bg-white/15 text-white border-white/25 backdrop-blur-sm">
              Build {APP_BUILD_DATE}
            </Badge>
            <Badge className="bg-white/15 text-white border-white/25 backdrop-blur-sm">
              <Sparkles size={11} className="mr-1" /> Live production
            </Badge>
          </div>
        </div>
      </Card>

      {/* Powered by */}
      <Card className="p-6 border-border bg-card" data-testid="about-poweredby">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Building2 size={22} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Powered by</p>
            <h3 className="mt-1 font-display text-xl font-semibold">
              SINDHE COMMUNICATION
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Building smart tools for CSC centers, internet cafes, photo studios and offices
              across Telangana &amp; India.
            </p>
          </div>
        </div>
      </Card>

      {/* Feature grid */}
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">What's inside</p>
        <h3 className="font-display text-xl font-semibold mt-1 mb-4">
          Everything a modern print shop needs
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <Card key={f.title} className="p-4 border-border bg-card hover:shadow-md transition-shadow" data-testid={`about-feature-${f.title.split(" ").join("-").toLowerCase()}`}>
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                  <Icon size={16} />
                </div>
                <p className="text-sm font-medium">{f.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{f.body}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Meta */}
      <Card className="p-6 border-border bg-card" data-testid="about-meta">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Version</p>
            <p className="mt-1 font-medium tabular-nums" data-testid="about-version">{APP_VERSION}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Build</p>
            <p className="mt-1 font-medium">{APP_BUILD_DATE}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Region</p>
            <p className="mt-1 font-medium">India</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Currency</p>
            <p className="mt-1 font-medium">INR (₹)</p>
          </div>
        </div>
        <Separator className="my-5" />
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <div>
            <p className="text-sm font-medium">Need help?</p>
            <p className="text-xs text-muted-foreground">Reach our support team on WhatsApp, email or phone.</p>
          </div>
          <Button asChild variant="outline" data-testid="about-goto-help">
            <a href="/help">
              Contact support <ExternalLink size={12} className="ml-2" />
            </a>
          </Button>
        </div>
      </Card>

      <p className="text-center text-xs text-muted-foreground pt-2">
        © {new Date().getFullYear()} SINDHE COMMUNICATION · MaaPrint is a trademark of SINDHE COMMUNICATION.
      </p>
    </div>
  );
}
